import { GameState } from './game/GameState';
import { Deck } from './game/Deck';
import { loadCards, loadClasses, loadTooltips, loadOpportunityCards, loadThinkingActions, loadRegrets, loadHelpTexts, loadGameName, loadStartingResources } from './data/loadData';
import { Renderer } from './ui/Renderer';
import { CardUI } from './ui/CardUI';
import { Modal } from './ui/Modal';
import { ClassSelection } from './game/ClassSelection';
import { RegretsPhase } from './game/RegretsPhase';
import { processEffects, applyCost } from './game/Effects';
import { checkCost } from './utils/CostChecker';
import { checkRequirements } from './utils/RequirementChecker';
import { getResourceIcon, getResourceLabel, getStatIcon, getStatLabel } from './utils/Icons';
import type { Card, Option, ThinkingAction, RegretsData, ResourceType, StatType, GameClass } from './types';

class Game {
  private gameState!: GameState;
  private deck: Deck | null = null;
  private renderer: Renderer | null = null;
  private cardUI: CardUI | null = null;
  private modal: Modal | null = null;
  private tooltips: any = {};
  private helpTexts: any = {};
  private opportunityCards: Card[] = [];
  private thinkingActions: any = null;
  private regretsData: RegretsData | null = null;
  private cards: Card[] = [];
  private classes: GameClass[] = [];
  private gameName: string = 'Choose Your Path';
  private isProcessingOption: boolean = false; // Guard against race conditions
  
  async init(): Promise<void> {
    // Load data
    this.cards = await loadCards();
    this.classes = await loadClasses();
    this.tooltips = await loadTooltips();
    this.helpTexts = await loadHelpTexts();
    this.opportunityCards = await loadOpportunityCards();
    this.thinkingActions = await loadThinkingActions();
    this.regretsData = await loadRegrets();
    this.gameName = await loadGameName();
    const startingResources = await loadStartingResources();
    
    // Initialize game state
    this.gameState = new GameState();
    this.gameState.initializeStartingResources(startingResources);
    this.gameState.setOnStateChange(() => this.render());
    
    // Initialize deck
    this.deck = new Deck(this.cards);
    
    // Initialize renderer
    const app = document.getElementById('app');
    if (!app) {
      throw new Error('App element not found');
    }
    
    this.renderer = new Renderer(app, this.gameState, this.deck, this.tooltips, this.helpTexts);
    this.renderer.setOnSkipToRegrets(() => {
      this.skipToRegrets();
    });
    this.renderer.setOnNewGame(() => {
      this.resetGame();
    });
    
    // Start with class selection
    this.showClassSelection(this.classes);
  }
  
  private resetGame(): void {
    // Completely reset game state
    this.gameState.reset();
    
    // Completely reset deck
    if (this.deck) {
      this.deck.reset(this.cards);
    }
    
    // Clear any modals
    document.querySelectorAll('.modal-overlay').forEach(el => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    
    // Reset UI components
    this.modal = null;
    this.cardUI = null;
    
    // Go back to class selection
    this.showClassSelection(this.classes);
  }
  
  private showClassSelection(classes: any[]): void {
    const app = document.getElementById('app');
    if (!app) return;
    
    const classSelection = new ClassSelection(
      this.gameState,
      classes,
      () => {
        this.startGame();
      },
      this.gameName
    );
    
    classSelection.render(app);
  }
  
  private startGame(): void {
    this.render();
  }
  
  private render(): void {
    if (!this.renderer || !this.deck) return;
    
    this.renderer.render();
    
    // If in card selection phase, render cards
    if (this.gameState.phase === 'card_selection') {
      const app = document.getElementById('app');
      if (!app) return;
      
      // Check win/lose conditions
      if (this.gameState.checkWinCondition()) {
        this.gameState.setPhase('game_over');
        this.render();
        return;
      }
      
      if (this.gameState.checkLoseCondition()) {
        this.gameState.setPhase('game_over');
        this.render();
        return;
      }
      
      // Check if deck is empty (but note: handleOptionSelect checks AFTER opportunity creation)
      // This check is for cases where render() is called from other places (state changes, etc.)
      // BUT: Don't start regrets if we're in the middle of processing an option (race condition guard)
      if (this.deck.isEmpty() && !this.isProcessingOption) {
        this.startRegretsPhase();
        return;
      }
      
      const cardsContainer = app.querySelector('#cards-container') as HTMLElement;
      if (cardsContainer) {
        cardsContainer.innerHTML = '';
        
        // Render cards
        this.cardUI = new CardUI(
          this.deck, 
          (card: Card, index: number) => {
            this.handleCardSelect(card, index);
          }
        );
        this.cardUI.render(cardsContainer);
      }
    }
  }
  
  private handleCardSelect(_card: Card, index: number): void {
    if (!this.deck) return;
    
    // Play the card (remove from deck)
    const playedCard = this.deck.playCard(index);
    if (!playedCard) return;
    
    // Store the original index for restoration
    const originalIndex = index;
    
    // Show modal with options
    this.modal = new Modal(
      this.gameState,
      this.deck,
      (option: Option) => {
        this.handleOptionSelect(playedCard, option);
      },
      () => {
        // If cancelled, put card back at the same position
        this.deck!.insertAt(originalIndex, playedCard);
        this.render();
      }
    );
    
    this.modal.show(playedCard);
  }
  
  private async handleOptionSelect(card: Card, option: Option): Promise<void> {
    if (!this.deck) return;
    
    // Set flag to prevent race conditions from state change callbacks
    this.isProcessingOption = true;
    
    try {
      // Validate requirements before proceeding
    if (option.requirements) {
      const tenacityNeeded = option.requirements.tenacity || 0;
      const smartsNeeded = option.requirements.smarts || 0;
      const socialNeeded = option.requirements.social || 0;
      
      const tenacityHave = this.gameState.getStat('tenacity');
      const smartsHave = this.gameState.getStat('smarts');
      const socialHave = this.gameState.getStat('social');
      const memoryAvailable = this.gameState.getResource('memory');
      
      // Check Tenacity (can use Memory)
      if (tenacityNeeded > 0) {
        const tenacityWithMemory = tenacityHave + memoryAvailable;
        if (tenacityWithMemory < tenacityNeeded) {
          console.error('Cannot meet Tenacity requirement even with Memory');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      
      // Check Smarts (cannot use Memory)
      if (smartsNeeded > 0 && smartsHave < smartsNeeded) {
        console.error('Cannot meet Smarts requirement');
        this.deck.addToTop(card);
        this.isProcessingOption = false;
        this.render();
        return;
      }
      
      // Check Social (cannot use Memory)
      if (socialNeeded > 0 && socialHave < socialNeeded) {
        console.error('Cannot meet Social requirement');
        this.deck.addToTop(card);
        this.isProcessingOption = false;
        this.render();
        return;
      }
      
      // Consume Memory if needed for Tenacity
      if (tenacityNeeded > 0 && tenacityHave < tenacityNeeded) {
        const memoryNeeded = tenacityNeeded - tenacityHave;
        if (memoryAvailable >= memoryNeeded) {
          this.gameState.modifyResource('memory', -memoryNeeded);
        }
      }
    }
    
    // Validate and apply cost (first option is free)
    const isFirstOption = card.options[0] === option;
    if (!isFirstOption && option.cost) {
      // Validate cost before applying
      const cost = option.cost;
      
      // Check time cost (only for non-free options)
      // Note: Current card is already removed from deck by playCard(), so use deck size directly
      if (cost.time !== undefined && cost.time > 0) {
        const availableToDiscard = this.deck.getDeckSize();
        if (availableToDiscard < cost.time) {
          console.error('Not enough cards for time cost');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      
      // Check resource costs (must have enough - no Sanity fallback)
      if (cost.money !== undefined && cost.money > 0) {
        if (this.gameState.getResource('money') < cost.money) {
          console.error('Not enough money');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      if (cost.food !== undefined && cost.food > 0) {
        if (this.gameState.getResource('food') < cost.food) {
          console.error('Not enough food');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      if (cost.motivation !== undefined && cost.motivation > 0) {
        if (this.gameState.getResource('motivation') < cost.motivation) {
          console.error('Not enough motivation');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      if (cost.memory !== undefined && cost.memory > 0) {
        if (this.gameState.getResource('memory') < cost.memory) {
          console.error('Not enough memory');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      if (cost.connections !== undefined && cost.connections > 0) {
        if (this.gameState.getResource('connections') < cost.connections) {
          console.error('Not enough connections');
          this.deck.addToTop(card);
          this.isProcessingOption = false;
          this.render();
          return;
        }
      }
      // Health and Sanity costs are always payable (can go negative)
      
      // Apply cost
      const costResult = applyCost(option.cost, this.gameState, this.deck);
      if (!costResult.success) {
        console.error('Failed to apply cost');
        this.deck.addToTop(card);
        this.isProcessingOption = false;
        this.render();
        return;
      }
      
      // Show toast notifications for discarded cards (time cost)
      if (costResult.discardedCards && costResult.discardedCards.length > 0) {
        for (const discardedCard of costResult.discardedCards) {
          this.showToast(`You did not have time for event: ${discardedCard.name}`);
        }
      }
    }
    
    // Check for level_up effect and handle it separately
    let hasLevelUp = false;
    let levelUpAmount = 0;
    if (option.effects) {
      for (const effect of option.effects) {
        if (effect && effect.level_up !== undefined) {
          hasLevelUp = true;
          levelUpAmount = effect.level_up;
          break;
        }
      }
    }
    
    // If level_up, show modal to choose stat
    if (hasLevelUp) {
      const selectedStat = await this.showLevelUpModal(levelUpAmount);
      if (selectedStat) {
        this.gameState.modifyStat(selectedStat, levelUpAmount);
      }
    }
    
    // Process effects (excluding level_up which we handled above)
    const filteredEffects = option.effects?.filter(effect => 
      !effect || effect.level_up === undefined
    );
    const effectResult = processEffects(filteredEffects, this.gameState, this.deck, card);
    
    // Handle special card actions
    if (effectResult.cardAction) {
      if (effectResult.cardAction.createOpportunity) {
        await this.handleCreateOpportunity();
      }
      if (effectResult.cardAction.unlockInspiration) {
        this.gameState.unlockInspiration();
      }
      if (effectResult.cardAction.thinkingModal) {
        await this.handleThinkingModal();
      }
      
      if (effectResult.cardAction.exhaust) {
        this.deck.exhaustCard(card);
      } else {
        this.deck.discardCard(card);
      }
    } else {
      // Default: discard
      this.deck.discardCard(card);
    }
    
    // Check for health collapse and show modal
    if (this.gameState.checkHealthCollapse()) {
      await this.showHealthCollapseModal();
    }
    
      // Check win/lose conditions
      if (this.gameState.checkWinCondition()) {
        this.gameState.setPhase('game_over');
        this.isProcessingOption = false;
        this.render();
        return;
      }
      
      if (this.gameState.checkLoseCondition()) {
        this.gameState.setPhase('game_over');
        this.isProcessingOption = false;
        this.render();
        return;
      }
      
      // Check if deck is empty AFTER opportunity cards are created
      // Regrets phase should only trigger when deck is empty AND no opportunity cards were just created
      if (this.deck.isEmpty()) {
        this.isProcessingOption = false; // Reset flag before starting regrets
        this.startRegretsPhase();
        return;
      }
      
      // Re-render
      this.render();
    } finally {
      // Always reset flag, even if we return early
      this.isProcessingOption = false;
    }
  }
  
  private showHealthCollapseModal(): Promise<void> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, rgba(100, 20, 20, 0.95) 0%, rgba(60, 10, 10, 0.95) 100%);
        border: 3px solid rgba(255, 74, 74, 0.6);
        border-radius: 16px;
        padding: 40px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 8px 32px rgba(255, 74, 74, 0.4);
      `;
      
      const icon = document.createElement('div');
      icon.style.cssText = `
        font-size: 48px;
        text-align: center;
        margin-bottom: 20px;
      `;
      icon.textContent = '💔';
      
      const title = document.createElement('h2');
      title.style.cssText = `
        font-size: 28px;
        color: #ff4a4a;
        margin-bottom: 20px;
        text-align: center;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      `;
      title.textContent = 'Worsening Condition';
      
      const message = document.createElement('p');
      message.style.cssText = `
        font-size: 16px;
        color: #ffcccc;
        line-height: 1.8;
        margin-bottom: 30px;
        text-align: center;
      `;
      message.textContent = 'Your health has collapsed. The physical strain has taken its toll on your mental state. Your Sanity has decreased by 1, and your Health has been stabilized at 1.';
      
      const button = document.createElement('button');
      button.style.cssText = `
        padding: 12px 30px;
        background: rgba(255, 74, 74, 0.3);
        border: 2px solid rgba(255, 74, 74, 0.6);
        border-radius: 8px;
        color: #fff;
        cursor: pointer;
        font-size: 16px;
        font-weight: bold;
        width: 100%;
        transition: all 0.2s ease;
      `;
      button.textContent = 'Continue';
      button.addEventListener('mouseenter', () => {
        button.style.background = 'rgba(255, 74, 74, 0.5)';
        button.style.borderColor = 'rgba(255, 74, 74, 0.8)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.background = 'rgba(255, 74, 74, 0.3)';
        button.style.borderColor = 'rgba(255, 74, 74, 0.6)';
      });
      button.addEventListener('click', () => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        resolve();
      });
      
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
          resolve();
        }
      });
      
      modal.appendChild(icon);
      modal.appendChild(title);
      modal.appendChild(message);
      modal.appendChild(button);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
  
  private skipToRegrets(): void {
    if (!this.deck) return;
    
    // Discard all remaining cards from top (excluding current if any)
    while (this.deck.getDeckSize() > 0) {
      const card = this.deck.playCard(0);
      if (card) {
        this.deck.discardCard(card);
      }
    }
    
    this.startRegretsPhase();
  }
  
  private async startRegretsPhase(): Promise<void> {
    if (!this.deck || !this.regretsData) return;

    this.gameState.setPhase('regrets');
    this.render();
    
    const regretsPhase = new RegretsPhase(
      this.gameState,
      this.deck,
      this.regretsData,
      () => {
        // After regrets phase, return to card selection
        this.gameState.setPhase('card_selection');
        this.render();
      }
    );
    
    await regretsPhase.start();
  }
  
  private async handleCreateOpportunity(): Promise<void> {
    if (!this.deck || this.opportunityCards.length === 0) return;
    
    // Pick a random opportunity card
    const randomIndex = Math.floor(Math.random() * this.opportunityCards.length);
    const opportunityCard = { ...this.opportunityCards[randomIndex] };
    
    // Add to top of deck
    this.deck.addToTop(opportunityCard);
    
    // Show notification
    await this.showNotification('New Opportunity', `A new opportunity has appeared: ${opportunityCard.name}`);
  }
  
  private async handleThinkingModal(): Promise<void> {
    if (!this.thinkingActions) return;
    
    // Get all actions - show both available and locked ones
    const alwaysAvailable = this.thinkingActions.always_available || [];
    const unlockedActions = this.thinkingActions.unlocked_actions || [];
    
    // Assign inspiration levels to unlocked actions (1, 2, 3 in order)
    const unlockedActionsWithLevels = unlockedActions.map((action: ThinkingAction, index: number) => {
      return {
        ...action,
        inspiration_required: index + 1 // First action needs 1, second needs 2, third needs 3
      };
    });
    
    // Combine all actions
    const allActions = [...alwaysAvailable, ...unlockedActionsWithLevels];
    
    // Filter out one-time actions that have already been used
    const availableActions = allActions.filter((action: ThinkingAction) => {
      if (action.one_time && this.gameState.isThinkingActionUsed(action.name)) {
        return false; // Hide one-time actions that have been used
      }
      return true;
    });
    
    // Show modal to select thinking action
    const selectedAction = await this.showThinkingModal(availableActions);
    
    if (selectedAction) {
      // Check if action is locked by inspiration
      const inspirationsUnlocked = this.gameState.getInspirationsUnlocked();
      if (selectedAction.inspiration_required !== undefined && inspirationsUnlocked < selectedAction.inspiration_required) {
        await this.showNotification('Locked', `This action requires ${selectedAction.inspiration_required} Inspiration. You have ${inspirationsUnlocked}.`);
        return;
      }
      
      // Apply cost
      if (selectedAction.cost) {
        const costResult = applyCost(selectedAction.cost, this.gameState, this.deck!);
        if (!costResult.success) {
          await this.showNotification('Cannot Afford', 'You cannot afford this action.');
          return;
        }
      }
      
      // Check requirements
      if (selectedAction.requirements) {
        const smartsNeeded = selectedAction.requirements.smarts || 0;
        const tenacityNeeded = selectedAction.requirements.tenacity || 0;
        const socialNeeded = selectedAction.requirements.social || 0;
        
        const smartsHave = this.gameState.getStat('smarts');
        const tenacityHave = this.gameState.getStat('tenacity');
        const socialHave = this.gameState.getStat('social');
        const memoryAvailable = this.gameState.getResource('memory');
        
        if (smartsNeeded > 0 && smartsHave < smartsNeeded) {
          await this.showNotification('Requirements Not Met', 'You do not meet the requirements for this action.');
          return;
        }
        if (tenacityNeeded > 0 && tenacityHave + memoryAvailable < tenacityNeeded) {
          await this.showNotification('Requirements Not Met', 'You do not meet the requirements for this action.');
          return;
        }
        if (socialNeeded > 0 && socialHave < socialNeeded) {
          await this.showNotification('Requirements Not Met', 'You do not meet the requirements for this action.');
          return;
        }
        
        // Consume Memory if needed for Tenacity
        if (tenacityNeeded > 0 && tenacityHave < tenacityNeeded) {
          const memoryNeeded = tenacityNeeded - tenacityHave;
          this.gameState.modifyResource('memory', -memoryNeeded);
        }
      }
      
      // Process effects (check for level_up in thinking actions too)
      if (selectedAction.effects) {
        let hasLevelUp = false;
        let levelUpAmount = 0;
        for (const effect of selectedAction.effects) {
          if (effect && effect.level_up !== undefined) {
            hasLevelUp = true;
            levelUpAmount = effect.level_up;
            break;
          }
        }
        
        if (hasLevelUp) {
          const selectedStat = await this.showLevelUpModal(levelUpAmount);
          if (selectedStat) {
            this.gameState.modifyStat(selectedStat, levelUpAmount);
          }
        }
        
        // Process other effects (excluding level_up)
        const filteredEffects = selectedAction.effects.filter(effect => 
          !effect || effect.level_up === undefined
        );
        processEffects(filteredEffects, this.gameState, this.deck!, {
          name: selectedAction.name,
          description: selectedAction.description
        });
      }
      
      // Mark as used if one-time
      if (selectedAction.one_time) {
        this.gameState.markThinkingActionUsed(selectedAction.name);
      }
    }
  }
  
  private showThinkingModal(actions: ThinkingAction[]): Promise<ThinkingAction | null> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        padding: 30px;
        max-width: 600px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      `;
      
      const title = document.createElement('h2');
      title.style.cssText = `
        font-size: 24px;
        color: #fff;
        margin-bottom: 10px;
      `;
      title.textContent = 'Evening Thoughts';
      
      const description = document.createElement('p');
      description.style.cssText = `
        font-size: 14px;
        color: #ccc;
        margin-bottom: 20px;
        line-height: 1.6;
      `;
      description.textContent = 'The evening brings quiet moments for reflection. What will you do?';
      
      const actionsList = document.createElement('div');
      actionsList.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 15px;
        margin-bottom: 20px;
      `;
      
      actions.forEach((action) => {
        const actionDiv = this.createThinkingActionElement(action, () => {
          document.body.removeChild(overlay);
          resolve(action);
        });
        actionsList.appendChild(actionDiv);
      });
      
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve(null);
        }
      });
      
      modal.appendChild(title);
      modal.appendChild(description);
      modal.appendChild(actionsList);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
  
  private createThinkingActionElement(action: ThinkingAction, onSelect: () => void): HTMLElement {
    const actionDiv = document.createElement('div');
    actionDiv.style.cssText = `
      background: rgba(0, 0, 0, 0.3);
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      padding: 20px;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    
    // Check if action is locked by inspiration requirement
    const inspirationsUnlocked = this.gameState.getInspirationsUnlocked();
    const isLockedByInspiration = action.inspiration_required !== undefined && inspirationsUnlocked < action.inspiration_required;
    
    // Check if can afford (only if not locked by inspiration)
    const costCheck = action.cost ? checkCost(action.cost, this.gameState, this.deck!) : { canAfford: true, needsSanity: false, sanityCost: 0 };
    const reqCheck = action.requirements ? checkRequirements(action.requirements, this.gameState) : { meetsRequirements: true };
    const canAfford = !isLockedByInspiration && costCheck.canAfford && reqCheck.meetsRequirements;
    
    if (!canAfford || isLockedByInspiration) {
      actionDiv.style.opacity = '0.5';
      actionDiv.style.cursor = 'not-allowed';
      actionDiv.style.pointerEvents = 'none';
      // Also add event listener as backup
      actionDiv.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, true); // Use capture phase
    } else {
      actionDiv.style.pointerEvents = 'auto';
      actionDiv.addEventListener('mouseenter', () => {
        actionDiv.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        actionDiv.style.background = 'rgba(0, 0, 0, 0.5)';
      });
      actionDiv.addEventListener('mouseleave', () => {
        actionDiv.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        actionDiv.style.background = 'rgba(0, 0, 0, 0.3)';
      });
      actionDiv.addEventListener('click', () => {
        if (canAfford) {
          onSelect();
        }
      });
    }
    
    const name = document.createElement('div');
    name.style.cssText = `
      font-size: 18px;
      font-weight: bold;
      color: ${canAfford ? '#fff' : '#888'};
      margin-bottom: 10px;
    `;
    name.textContent = action.name;
    
    const desc = document.createElement('div');
    desc.style.cssText = `
      font-size: 14px;
      color: #ccc;
      margin-bottom: 10px;
      line-height: 1.5;
    `;
    desc.textContent = action.description;
    
    const details = document.createElement('div');
    details.style.cssText = `
      font-size: 11px;
      color: #888;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `;
    
    if (action.cost) {
      const costContainer = this.createCostDisplayForThinking(action.cost);
      if (costContainer) {
        details.appendChild(costContainer);
      }
    }
    
    // Show inspiration requirement if locked
    if (action.inspiration_required !== undefined) {
      const inspirationReqContainer = document.createElement('div');
      inspirationReqContainer.style.cssText = `
        display: flex;
        flex-wrap: wrap;
        align-items: center;
      `;
      
      const label = document.createElement('span');
      label.style.cssText = 'color: #888; margin-right: 4px;';
      label.textContent = 'Requires: ';
      inspirationReqContainer.appendChild(label);
      
      // Use the inspirationsUnlocked variable declared at the top of the function
      const isUnlocked = inspirationsUnlocked >= action.inspiration_required;
      const inspirationReq = document.createElement('span');
      inspirationReq.style.cssText = `
        color: ${isUnlocked ? '#4aff9f' : '#ff4a4a'};
        font-weight: ${isUnlocked ? 'normal' : 'bold'};
        display: inline-flex;
        align-items: center;
        gap: 4px;
      `;
      inspirationReq.innerHTML = `💡 <strong>Inspiration: ${action.inspiration_required}</strong> (have: ${inspirationsUnlocked})`;
      inspirationReqContainer.appendChild(inspirationReq);
      
      details.appendChild(inspirationReqContainer);
    }
    
    if (action.requirements) {
      const reqContainer = this.createRequirementDisplayForThinking(action.requirements);
      if (reqContainer) {
        details.appendChild(reqContainer);
      }
    }
    
    if (action.one_time) {
      const oneTimeLabel = document.createElement('span');
      oneTimeLabel.style.cssText = `
        color: #ffaa00;
        font-weight: bold;
      `;
      oneTimeLabel.textContent = '(One-time)';
      details.appendChild(oneTimeLabel);
    }
    
    // Show effects
    if (action.effects && action.effects.length > 0) {
      const effectsContainer = this.createEffectsDisplayForThinking(action.effects);
      if (effectsContainer) {
        details.appendChild(effectsContainer);
      }
    }
    
    actionDiv.appendChild(name);
    actionDiv.appendChild(desc);
    actionDiv.appendChild(details);
    
    return actionDiv;
  }
  
  private createCostDisplayForThinking(cost: any): HTMLElement | null {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; margin-right: 4px;';
    label.textContent = 'Cost:';
    container.appendChild(label);
    
    const costParts: HTMLElement[] = [];
    
    if (cost.money) {
      const elem = this.createResourceCostElemForThinking('money', cost.money);
      costParts.push(elem);
    }
    if (cost.food) {
      const elem = this.createResourceCostElemForThinking('food', cost.food);
      costParts.push(elem);
    }
    if (cost.motivation) {
      const elem = this.createResourceCostElemForThinking('motivation', cost.motivation);
      costParts.push(elem);
    }
    if (cost.memory) {
      const elem = this.createResourceCostElemForThinking('memory', cost.memory);
      costParts.push(elem);
    }
    if (cost.connections) {
      const elem = this.createResourceCostElemForThinking('connections', cost.connections);
      costParts.push(elem);
    }
    if (cost.health) {
      const elem = this.createResourceCostElemForThinking('health', cost.health);
      costParts.push(elem);
    }
    if (cost.sanity) {
      const elem = this.createResourceCostElemForThinking('sanity', cost.sanity);
      costParts.push(elem);
    }
    if (cost.time) {
      const span = document.createElement('span');
      span.style.cssText = 'color: #4aff9f;';
      span.textContent = `Time: ${cost.time}`;
      costParts.push(span);
    }
    
    if (costParts.length === 0) {
      return null;
    }
    
    costParts.forEach((elem, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.style.cssText = 'color: #888; margin: 0 4px 0 2px;';
        separator.textContent = ',';
        container.appendChild(separator);
      }
      container.appendChild(elem);
    });
    
    return container;
  }
  
  private createResourceCostElemForThinking(resource: string, needed: number): HTMLElement {
    const span = document.createElement('span');
    const resourceType = resource as ResourceType;
    const icon = getResourceIcon(resourceType);
    const label = getResourceLabel(resourceType);
    const have = this.gameState.getResource(resourceType);
    
    // Health and Sanity costs are always payable
    const isAlwaysPayable = resource === 'health' || resource === 'sanity';
    const canAfford = isAlwaysPayable ? true : have >= needed;
    
    let color = '#4aff9f';
    if (isAlwaysPayable) {
      color = resource === 'health' ? '#ff4a4a' : '#aa4aff';
    } else {
      color = canAfford ? '#4aff9f' : '#ff4a4a';
    }
    
    span.style.cssText = `
      color: ${color};
      font-weight: ${canAfford ? 'normal' : 'bold'};
      display: inline-flex;
      align-items: center;
      gap: 4px;
    `;
    
    if (icon) {
      const payableText = isAlwaysPayable ? ' (always payable)' : '';
      span.innerHTML = `<strong>${label}: -${needed}</strong> ${icon}${payableText ? ` <span style="color: #888; font-size: 0.9em;">${payableText}</span>` : ''}`;
    } else {
      const payableText = isAlwaysPayable ? ' (always payable)' : '';
      span.textContent = `${label}: -${needed}${payableText}`;
    }
    
    return span;
  }
  
  private createRequirementDisplayForThinking(req: any): HTMLElement | null {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; margin-right: 4px;';
    label.textContent = 'Requires:';
    container.appendChild(label);
    
    const reqParts: HTMLElement[] = [];
    
    if (req.smarts) {
      const elem = this.createStatReqElemForThinking('smarts', req.smarts);
      reqParts.push(elem);
    }
    if (req.tenacity) {
      const elem = this.createStatReqElemForThinking('tenacity', req.tenacity);
      reqParts.push(elem);
    }
    if (req.social) {
      const elem = this.createStatReqElemForThinking('social', req.social);
      reqParts.push(elem);
    }
    
    if (reqParts.length === 0) {
      return null;
    }
    
    reqParts.forEach((elem, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.style.cssText = 'color: #888; margin: 0 4px 0 2px;';
        separator.textContent = ',';
        container.appendChild(separator);
      }
      container.appendChild(elem);
    });
    
    return container;
  }
  
  private createStatReqElemForThinking(stat: string, needed: number): HTMLElement {
    const span = document.createElement('span');
    const statType = stat as StatType;
    const icon = getStatIcon(statType);
    const label = getStatLabel(statType);
    const have = this.gameState.getStat(statType);
    const meetsReq = have >= needed;
    
    span.style.cssText = `
      color: ${meetsReq ? '#4aff9f' : '#ff4a4a'};
      font-weight: ${meetsReq ? 'normal' : 'bold'};
      display: inline-flex;
      align-items: center;
      gap: 4px;
    `;
    
    if (icon) {
      span.innerHTML = `${icon} <strong>${label}: ${needed}</strong> (have: ${have})`;
    } else {
      span.textContent = `${label}: ${needed} (have: ${have})`;
    }
    
    return span;
  }
  
  private createEffectsDisplayForThinking(effects: any[]): HTMLElement | null {
    const container = document.createElement('div');
    container.style.cssText = `
      margin-top: 10px;
      font-size: 11px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    `;
    
    const label = document.createElement('span');
    label.style.cssText = 'color: #888; font-weight: bold; margin-right: 4px;';
    label.textContent = 'Effects:';
    container.appendChild(label);
    
    const effectParts: HTMLElement[] = [];
    
    for (const effect of effects) {
      if (!effect || typeof effect !== 'object') continue;
      
      // Resource effects
      if (effect.money !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('money', effect.money));
      }
      if (effect.food !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('food', effect.food));
      }
      if (effect.motivation !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('motivation', effect.motivation));
      }
      if (effect.memory !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('memory', effect.memory));
      }
      if (effect.connections !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('connections', effect.connections));
      }
      if (effect.health !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('health', effect.health));
      }
      if (effect.bad_habit !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('bad_habit', effect.bad_habit));
      }
      if (effect.sanity !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('sanity', effect.sanity));
      }
      if (effect.dignity !== undefined) {
        effectParts.push(this.createResourceEffectElemForThinking('dignity', effect.dignity));
      }
      
      // Stat effects
      if (effect.level_up !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f;';
        span.textContent = `Choose stat to level up: +${effect.level_up}`;
        effectParts.push(span);
      }
      if (effect.level_up_tenacity !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `👁️ <strong>Tenacity: +${effect.level_up_tenacity}</strong>`;
        effectParts.push(span);
      }
      if (effect.level_up_smarts !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `${getStatIcon('smarts')} <strong>Smarts: +${effect.level_up_smarts}</strong>`;
        effectParts.push(span);
      }
      if (effect.level_up_social !== undefined) {
        const span = document.createElement('span');
        span.style.cssText = 'color: #4aff9f; display: inline-flex; align-items: center; gap: 4px;';
        span.innerHTML = `💬 <strong>Social: +${effect.level_up_social}</strong>`;
        effectParts.push(span);
      }
    }
    
    if (effectParts.length === 0) {
      return null;
    }
    
    effectParts.forEach((elem, index) => {
      if (index > 0) {
        const separator = document.createElement('span');
        separator.style.cssText = 'color: #888; margin: 0 4px 0 2px;';
        separator.textContent = ',';
        container.appendChild(separator);
      }
      container.appendChild(elem);
    });
    
    return container;
  }
  
  private createResourceEffectElemForThinking(resource: string, value: number): HTMLElement {
    const span = document.createElement('span');
    const resourceType = resource as ResourceType;
    const icon = getResourceIcon(resourceType);
    const label = getResourceLabel(resourceType);
    const isPositive = value > 0;
    const isNegative = value < 0;
    
    // Bad Habits is a negative resource, so colors are flipped
    const isBadHabit = resource === 'bad_habit';
    let color = '#fff';
    if (isBadHabit) {
      color = isPositive ? '#ff4a4a' : isNegative ? '#4aff9f' : '#fff';
    } else {
      color = isPositive ? '#4aff9f' : isNegative ? '#ff4a4a' : '#fff';
    }
    
    span.style.cssText = `
      color: ${color};
      display: inline-flex;
      align-items: center;
      gap: 4px;
    `;
    
    if (icon) {
      span.innerHTML = `<strong>${label}: ${value > 0 ? '+' : ''}${value}</strong> ${icon}`;
    } else {
      span.textContent = `${label}: ${value > 0 ? '+' : ''}${value}`;
    }
    
    return span;
  }
  
  private showLevelUpModal(amount: number): Promise<'smarts' | 'tenacity' | 'social' | null> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        padding: 30px;
        max-width: 400px;
        width: 90%;
      `;
      
      const title = document.createElement('h2');
      title.style.cssText = `
        font-size: 24px;
        color: #fff;
        margin-bottom: 15px;
        text-align: center;
      `;
      title.textContent = 'Level Up';
      
      const message = document.createElement('p');
      message.style.cssText = `
        font-size: 16px;
        color: #ccc;
        margin-bottom: 20px;
        text-align: center;
      `;
      message.textContent = `Choose which stat to increase by ${amount}:`;
      
      const statsContainer = document.createElement('div');
      statsContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 20px;
      `;
      
      const stats = [
        { key: 'smarts' as const, label: 'Smarts', symbol: getStatIcon('smarts'), value: this.gameState.getStat('smarts') },
        { key: 'tenacity' as const, label: 'Tenacity', symbol: '👁️', value: this.gameState.getStat('tenacity') },
        { key: 'social' as const, label: 'Social', symbol: '💬', value: this.gameState.getStat('social') }
      ];
      
      stats.forEach((stat) => {
        const button = document.createElement('button');
        button.style.cssText = `
          padding: 15px;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #fff;
          cursor: pointer;
          font-size: 16px;
          text-align: left;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 10px;
        `;
        
        const symbol = document.createElement('span');
        symbol.textContent = stat.symbol;
        symbol.style.fontSize = '20px';
        
        const text = document.createElement('span');
        text.textContent = `${stat.label} (${stat.value} → ${stat.value + amount})`;
        
        button.appendChild(symbol);
        button.appendChild(text);
        
        button.addEventListener('mouseenter', () => {
          button.style.background = 'rgba(255, 255, 255, 0.2)';
          button.style.borderColor = 'rgba(255, 255, 255, 0.4)';
        });
        button.addEventListener('mouseleave', () => {
          button.style.background = 'rgba(255, 255, 255, 0.1)';
          button.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        });
        button.addEventListener('click', () => {
          if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
          resolve(stat.key);
        });
        
        statsContainer.appendChild(button);
      });
      
      const cancelButton = document.createElement('button');
      cancelButton.style.cssText = `
        padding: 12px 24px;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        color: #fff;
        cursor: pointer;
        font-size: 16px;
        transition: all 0.2s ease;
        width: 100%;
      `;
      cancelButton.textContent = 'Cancel';
      cancelButton.addEventListener('mouseenter', () => {
        cancelButton.style.background = 'rgba(255, 255, 255, 0.2)';
      });
      cancelButton.addEventListener('mouseleave', () => {
        cancelButton.style.background = 'rgba(255, 255, 255, 0.1)';
      });
      cancelButton.addEventListener('click', () => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        resolve(null);
      });
      
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
          }
          resolve(null);
        }
      });
      
      modal.appendChild(title);
      modal.appendChild(message);
      modal.appendChild(statsContainer);
      modal.appendChild(cancelButton);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
  
  private showToast(message: string, duration: number = 7000): void {
    // Add animation keyframes if not already added
    if (!document.getElementById('toast-animations')) {
      const style = document.createElement('style');
      style.id = 'toast-animations';
      style.textContent = `
        @keyframes toastSlideIn {
          from {
            transform: translateX(-50%) translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        @keyframes toastSlideOut {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(100px);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Get existing toasts to calculate offset
    const existingToasts = document.querySelectorAll('.game-toast');
    const toastOffset = existingToasts.length * 70; // 70px spacing between toasts
    
    const toast = document.createElement('div');
    toast.className = 'game-toast';
    toast.style.cssText = `
      position: fixed;
      bottom: ${30 + toastOffset}px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, rgba(42, 42, 42, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      padding: 16px 24px;
      color: #fff;
      font-size: 18px;
      z-index: 10000;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
      max-width: 500px;
      text-align: center;
      animation: toastSlideIn 0.24s ease-out;
      pointer-events: none;
      word-wrap: break-word;
    `;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // Remove after duration
    setTimeout(() => {
      toast.style.animation = 'toastSlideOut 0.24s ease-out';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
        // Reposition remaining toasts
        const remainingToasts = document.querySelectorAll('.game-toast');
        remainingToasts.forEach((remainingToast, index) => {
          (remainingToast as HTMLElement).style.bottom = `${30 + index * 70}px`;
        });
      }, 240);
    }, duration);
  }
  
  private showNotification(title: string, message: string): Promise<void> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        padding: 30px;
        max-width: 400px;
        width: 90%;
      `;
      
      const titleElem = document.createElement('h2');
      titleElem.style.cssText = `
        font-size: 20px;
        color: #fff;
        margin-bottom: 15px;
      `;
      titleElem.textContent = title;
      
      const messageElem = document.createElement('p');
      messageElem.style.cssText = `
        font-size: 14px;
        color: #ccc;
        line-height: 1.6;
        margin-bottom: 20px;
      `;
      messageElem.textContent = message;
      
      const button = document.createElement('button');
      button.style.cssText = `
        padding: 10px 20px;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        color: #fff;
        cursor: pointer;
        font-size: 14px;
      `;
      button.textContent = 'OK';
      button.addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve();
      });
      
      modal.appendChild(titleElem);
      modal.appendChild(messageElem);
      modal.appendChild(button);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
}

// Initialize game when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const game = new Game();
  game.init().catch(console.error);
});

